import { FormGroup } from '@ksyos/design-system';
import { values } from 'lodash';
import { useEffect, useState } from 'react';
import type { IServiceGetOne } from '@frontend/components/interfaces';
import type { IOption } from '@frontend/components/interfaces/option';
import { tcohV2Advies } from '@common/customControls/TCOH/v2/tcohV2Advies';
import { tcohV2RelatieEcgEnKlachten } from '@common/customControls/TCOH/v2/tcohV2RelatieEcgEnKlachten';
import { maxOverigeOpmerkingenLength } from '@common/validation/customControls/cardiology/holterConsult/cardiology';
import { TypeaheadInput } from '@frontend/components/fields/InputTypeahead';
import { SelectFormGroupField } from '@frontend/components/fields/SelectField';
import { TextAreaFormGroupField } from '@frontend/components/fields/TextAreaField';
import type { AdviesConsult } from '../../../../../../common/interfaces/adviesConsult';
import type { ValidationErrors } from '../../../../../utils/validation';
import { tcohV2Conclusie } from '../../../../../../common/customControls/TCOH/v2/tcohV2Conclusie';
import { getSelectedLanguage } from '../../../../../translate';
import { AdviesConsultAddEdit, emptyAdviesConsult } from './AdviesConsultAddEdit';

export interface ICustomControlData {
    advies: string | null;
    adviesConsult?: AdviesConsult | null;
    conclusie: tcohV2Conclusie | null;
    overigeOpmerkingen: string | null;
    relatieEcgEnKlachten: string | null;
}

interface ITcohV2DiagnosticReportAddEditCustomControlFieldsProps {
    adviesBijAfwijkendeBeoordelingVraag?: string;
    customControlData: ICustomControlData;
    errors: ValidationErrors;
    handleChangeCustomControl: (values: Partial<ICustomControlData>) => Promise<void>;
    isAdviesBijAfwijkendeBeoordelingRequested: boolean;
    service: IServiceGetOne;
    validateOneCustomControl: (name: string) => Promise<void>;
}

export function TcohV2DiagnosticReportAddEditCustomControlFields(
    props: ITcohV2DiagnosticReportAddEditCustomControlFieldsProps
) {
    const translate = getSelectedLanguage();
    const [isAdviesConsultEnabled, setIsAdviesConsultEnabled] = useState(false);

    useEffect(() => {
        setIsAdviesConsultEnabled(props.isAdviesBijAfwijkendeBeoordelingRequested && isAfwijkendeBeoordeling());
    }, [props.isAdviesBijAfwijkendeBeoordelingRequested, props.customControlData.conclusie]);

    useEffect(() => {
        if (
            props.customControlData.conclusie !== null &&
            isAfwijkendeBeoordeling() === false &&
            Boolean(props.customControlData.adviesConsult)
        ) {
            props.handleChangeCustomControl({
                adviesConsult: null,
            });
        }
    }, [props.customControlData.conclusie]);

    return (
        <>
            <SelectFormGroupField
                fieldKey="conclusie"
                isClearable={false}
                isRequired={true}
                label={translate.common.conclusion}
                onChange={async (value) => await handleConclusionChange(value as tcohV2Conclusie)}
                options={conclusionSelectOptions()}
                placeholder={translate.common.clickAndChooseFromList}
            />

            {isAdviesConsultEnabled === false && (
                <FormGroup
                    hasErrors={props.errors.has('customControlData.advies')}
                    label={translate.diagnosticReport.advice}
                >
                    <TypeaheadInput
                        error={props.errors.get('customControlData.advies')}
                        onChange={async (value) => await handleAdviceChange(value)}
                        onDoValidate={async () => await props.validateOneCustomControl('advies')}
                        options={getAdviceOptions(props.customControlData.conclusie)}
                        placeholder={translate.common.describeOrChooseFromList}
                        value={props.customControlData.advies}
                        variant={'outline'}
                    />
                </FormGroup>
            )}

            <FormGroup
                hasErrors={props.errors.has('customControlData.relatieEcgEnKlachten')}
                label={translate.diagnosticReport.relationEcgAndComplaints}
            >
                <TypeaheadInput
                    error={props.errors.get('customControlData.relatieEcgEnKlachten')}
                    onChange={async (value) => await props.handleChangeCustomControl({ relatieEcgEnKlachten: value })}
                    onDoValidate={async () => await props.validateOneCustomControl('relatieEcgEnKlachten')}
                    options={relationEcgAndComplaintsOptions()}
                    placeholder={translate.common.describeOrChooseFromList}
                    value={props.customControlData.relatieEcgEnKlachten}
                    variant={'outline'}
                />
            </FormGroup>

            <TextAreaFormGroupField
                fieldKey="overigeOpmerkingen"
                label={translate.diagnosticReport.additionalRemarks}
                maxLength={maxOverigeOpmerkingenLength}
                placeholder={`${translate.diagnosticReport.additionalRemarks}...`}
                rows={4}
                variant="outline"
            />

            {isAdviesConsultEnabled && (
                <AdviesConsultAddEdit
                    adviesConsult={props.customControlData.adviesConsult ?? emptyAdviesConsult}
                    adviesOptions={getAdviceOptions(props.customControlData.conclusie, isAdviesConsultEnabled)}
                    errors={props.errors}
                    onChange={async (adviesConsult) => props.handleChangeCustomControl({ adviesConsult })}
                    service={props.service}
                    validateOneCustomControl={(name) => props.validateOneCustomControl(name)}
                    vraag={props.adviesBijAfwijkendeBeoordelingVraag ?? ''}
                />
            )}
        </>
    );

    async function handleConclusionChange(conclusie: tcohV2Conclusie | null) {
        if (conclusie === tcohV2Conclusie.alarmerend_afwijkend) {
            await props.handleChangeCustomControl({
                advies: tcohV2Advies.spoed_verwijs_patient_naar_eerste_harthulp,
            });

            if (isAdviesConsultEnabled) {
                await props.handleChangeCustomControl({
                    adviesConsult: {
                        ...(props.customControlData.adviesConsult ?? emptyAdviesConsult),
                        advies: tcohV2Advies.spoed_verwijs_patient_naar_eerste_harthulp,
                    },
                });
            }
        } else {
            if (props.customControlData.advies === tcohV2Advies.spoed_verwijs_patient_naar_eerste_harthulp) {
                await props.handleChangeCustomControl({
                    advies: null,
                });
            }

            if (
                props.customControlData.adviesConsult?.advies ===
                tcohV2Advies.spoed_verwijs_patient_naar_eerste_harthulp
            ) {
                await props.handleChangeCustomControl({
                    adviesConsult: { ...props.customControlData.adviesConsult, advies: null },
                });
            }
        }

        await props.handleChangeCustomControl({ conclusie });
    }

    function isAfwijkendeBeoordeling() {
        return (
            props.customControlData.conclusie === tcohV2Conclusie.afwijkend ||
            props.customControlData.conclusie === tcohV2Conclusie.alarmerend_afwijkend
        );
    }

    async function handleAdviceChange(advies: string | null) {
        let adviesValue = advies;

        if (props.customControlData.conclusie === tcohV2Conclusie.alarmerend_afwijkend) {
            adviesValue = tcohV2Advies.spoed_verwijs_patient_naar_eerste_harthulp;
        }

        await props.handleChangeCustomControl({ advies: adviesValue });
    }
}

export function conclusionSelectOptions(): IOption<string>[] {
    return convertToSelectOptions(values(tcohV2Conclusie));
}

function convertToSelectOptions(options: string[]) {
    return options.map((value) => ({ label: value, value }));
}

export function getAdviceOptions(conclusie: tcohV2Conclusie | null, isAdviesConsultEnabled: boolean = false) {
    if (conclusie === tcohV2Conclusie.alarmerend_afwijkend) {
        return [tcohV2Advies.spoed_verwijs_patient_naar_eerste_harthulp];
    }

    const excludedOptions = [tcohV2Advies.spoed_verwijs_patient_naar_eerste_harthulp];

    if (isAdviesConsultEnabled) {
        excludedOptions.push(tcohV2Advies.overweeg_opvolgend_teleconsult_cardioloog_via_ksyos);
    }

    return Object.values(tcohV2Advies).filter((value) => excludedOptions.includes(value) === false);
}

export function relationEcgAndComplaintsOptions() {
    return Object.values(tcohV2RelatieEcgEnKlachten);
}

export function tcohV2DiagnosticReportInitializeCustomControlData() {
    return {
        advies: null,
        adviesConsult: null,
        conclusie: null,
        overigeOpmerkingen: null,
        relatieEcgEnKlachten: null,
    };
}
